import React, { useState } from 'react';
import { useFeatures } from '../../context/FeatureContext';

const FeatureSettings = () => {
  const {
    features,
    loading,
    error,
    updateFeatureConfig,
    enableFeature,
    disableFeature
  } = useFeatures();

  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  const featureDescriptions = {
    microphones: 'Voice recording and microphone management',
    conversations: 'AI conversation and voice interaction',
    automations: 'Automation rules and triggers',
    scenes: 'Scene management and control',
    devices: 'Device management and control',
    smartLocks: 'Smart lock integration',
    remoteAccess: 'FRP remote access configuration',
    systemSettings: 'System configuration and settings'
  };

  const handleToggleFeature = async (featureName, currentlyEnabled) => {
    try {
      setIsUpdating(true);
      setUpdateError(null);

      const result = currentlyEnabled 
        ? await disableFeature(featureName)
        : await enableFeature(featureName);

      if (!result.success) {
        setUpdateError(result.error || 'Failed to update feature');
      }
    } catch (err) {
      setUpdateError('Failed to update feature: ' + err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="feature-settings">
        <h3>Feature Management</h3>
        <div className="loading-spinner">Loading feature configuration...</div>
      </div>
    );
  }

  return (
    <div className="feature-settings">
      <h3>Feature Management</h3>
      <p className="description">
        Control which features are available in the smart home interface. 
        Disabled features will be hidden from the navigation and inaccessible to users.
      </p>

      {(error || updateError) && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error || updateError}
        </div>
      )}

      <div className="features-list">
        {Object.entries(features).map(([featureName, featureConfig]) => (
          <div key={featureName} className="feature-item">
            <div className="feature-info">
              <div className="feature-header">
                <h4 className="feature-name">
                  {featureName.charAt(0).toUpperCase() + featureName.slice(1).replace(/([A-Z])/g, ' $1')}
                </h4>
                <div className="feature-toggle">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={featureConfig.enabled || false}
                      onChange={() => handleToggleFeature(featureName, featureConfig.enabled)}
                      disabled={isUpdating}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className={`status-text ${featureConfig.enabled ? 'enabled' : 'disabled'}`}>
                    {featureConfig.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
              <p className="feature-description">
                {featureConfig.description || featureDescriptions[featureName] || 'No description available'}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="feature-info-section">
        <h4>Important Notes</h4>
        <ul>
          <li>Disabling a feature will hide it from the navigation menu and make it inaccessible</li>
          <li>Users currently viewing a disabled feature will be redirected to the settings page</li>
          <li>System Settings cannot be disabled to ensure access to this configuration panel</li>
          <li>Changes take effect immediately without requiring a system restart</li>
        </ul>
      </div>

      <style jsx>{`
        .feature-settings {
          padding: 20px;
          max-width: 800px;
        }

        .description {
          color: #666;
          margin-bottom: 30px;
          line-height: 1.6;
        }

        .alert {
          padding: 12px;
          border-radius: 4px;
          margin-bottom: 20px;
        }

        .alert-error {
          background-color: #f8d7da;
          border: 1px solid #f5c6cb;
          color: #721c24;
        }

        .features-list {
          margin-bottom: 40px;
        }

        .feature-item {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 15px;
          transition: box-shadow 0.2s;
        }

        .feature-item:hover {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .feature-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }

        .feature-name {
          margin: 0;
          color: #495057;
          font-size: 18px;
          font-weight: 600;
        }

        .feature-toggle {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 50px;
          height: 24px;
        }

        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.3s;
          border-radius: 24px;
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
        }

        input:checked + .toggle-slider {
          background-color: #28a745;
        }

        input:checked + .toggle-slider:before {
          transform: translateX(26px);
        }

        input:disabled + .toggle-slider {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .status-text {
          font-weight: 500;
          font-size: 14px;
        }

        .status-text.enabled {
          color: #28a745;
        }

        .status-text.disabled {
          color: #dc3545;
        }

        .feature-description {
          color: #6c757d;
          margin: 0;
          line-height: 1.5;
          font-size: 14px;
        }

        .feature-info-section {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          border: 1px solid #dee2e6;
        }

        .feature-info-section h4 {
          color: #495057;
          margin-bottom: 15px;
        }

        .feature-info-section ul {
          margin: 0;
          padding-left: 20px;
        }

        .feature-info-section li {
          color: #6c757d;
          margin-bottom: 8px;
          line-height: 1.5;
        }

        .loading-spinner {
          text-align: center;
          padding: 40px;
          color: #6c757d;
        }
      `}</style>
    </div>
  );
};

export default FeatureSettings;